import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, Button } from 'reactstrap';
import { FaGlobe, FaCheck, FaArrowRight, FaTimes } from 'react-icons/fa';
import { useChangeLanguage } from '../../hook/useChangeLanguage';
import './LanguageSwitcher.css';

// English-name lookup for the confirmation copy ("shown in Arabic" / "بالإنجليزية"),
// separate from LANGUAGES' nativeName below — the compare boxes always show a
// language's own native spelling, but the sentence around them is read in
// whichever language is CURRENTLY active (i.e. before the switch happens), so it
// needs the target language's name translated into that current language.
const LANGUAGE_NAME_KEY: Record<'en' | 'ar', string> = {
  en: 'languageSwitch.langNameEn',
  ar: 'languageSwitch.langNameAr',
};

// Native spellings only — UAT-CS-01 requires each language's own name to stay in
// its own script regardless of which language is currently active, so these are
// deliberately not run through t(). shortCode is the closed-trigger's compact
// "EN | ع" display; the dropdown itself still lists the full native names.
const LANGUAGES: Array<{ code: 'en' | 'ar'; nativeName: string; shortCode: string }> = [
  { code: 'en', nativeName: 'English', shortCode: 'EN' },
  { code: 'ar', nativeName: 'العربية', shortCode: 'ع' },
];

interface LanguageSwitcherProps {
  /** 'light' (default) for light surfaces like the header; 'dark' for the login/register hero panels. */
  variant?: 'light' | 'dark';
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'light', className }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { requestChangeLanguage, pendingLanguage, pendingWasDirty, confirmSwitch, cancelSwitch } = useChangeLanguage();

  const current = i18n.resolvedLanguage || i18n.language || 'en';
  const currentOption = LANGUAGES.find((l) => current.startsWith(l.code)) || LANGUAGES[0];
  const pendingOption = LANGUAGES.find((l) => l.code === pendingLanguage) || null;

  const handleSelect = (code: string) => {
    setIsOpen(false);
    if (code === currentOption.code) return;
    requestChangeLanguage(code);
  };

  return (
    <>
      <Dropdown
        isOpen={isOpen}
        toggle={() => setIsOpen((v) => !v)}
        className={`lang-switcher lang-switcher--${variant} ${className || ''}`}
      >
        <DropdownToggle
          tag="button"
          type="button"
          className="lang-switcher-trigger lang-switcher-trigger--compact"
          aria-haspopup={true}
          aria-expanded={isOpen}
          aria-label={t('languageSwitch.ariaLabel') as string}
        >
          {LANGUAGES.map((lang, index) => (
            <React.Fragment key={lang.code}>
              {index > 0 && (
                <span className="lang-switcher-sep" aria-hidden="true">
                  |
                </span>
              )}
              <span
                className={`lang-switcher-code${lang.code === currentOption.code ? ' lang-switcher-code--active' : ''}`}
              >
                {lang.shortCode}
              </span>
            </React.Fragment>
          ))}
        </DropdownToggle>
        <DropdownMenu role="menu" end className="lang-switcher-menu">
          {LANGUAGES.map((lang) => {
            const isCurrent = lang.code === currentOption.code;
            return (
              <DropdownItem
                key={lang.code}
                role="menuitem"
                active={isCurrent}
                onClick={() => handleSelect(lang.code)}
                className="lang-switcher-item"
              >
                <span className="lang-switcher-check">{isCurrent && <FaCheck size={11} aria-hidden="true" />}</span>
                <span>{lang.nativeName}</span>
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      </Dropdown>

      <Modal isOpen={!!pendingLanguage} toggle={cancelSwitch} centered contentClassName="lang-confirm-modal">
        <button
          type="button"
          className="lang-confirm-close"
          onClick={cancelSwitch}
          aria-label={t('common.close') as string}
        >
          <FaTimes size={14} aria-hidden="true" />
        </button>

        <div className="lang-confirm-icon">
          <FaGlobe size={20} aria-hidden="true" />
        </div>

        <h2 className="lang-confirm-title">{t('languageSwitch.modalTitle')}</h2>
        <p className="lang-confirm-subtitle">
          {pendingOption &&
            t('languageSwitch.modalSubtitle', {
              language: t(LANGUAGE_NAME_KEY[pendingOption.code]),
            })}
        </p>

        <div className="lang-confirm-compare">
          <div className="lang-confirm-box">
            <span className="lang-confirm-box-label">{t('languageSwitch.current')}</span>
            <span className="lang-confirm-box-value">{currentOption.nativeName}</span>
          </div>
          <span className="lang-confirm-arrow" aria-hidden="true">
            <FaArrowRight size={12} />
          </span>
          <div className="lang-confirm-box lang-confirm-box--new">
            <span className="lang-confirm-box-label">{t('languageSwitch.new')}</span>
            <span className="lang-confirm-box-value">{pendingOption?.nativeName}</span>
          </div>
        </div>

        <div className="lang-confirm-note">
          {pendingOption &&
            t(pendingOption.code === 'ar' ? 'languageSwitch.modalNoteRtl' : 'languageSwitch.modalNoteLtr')}
          {pendingWasDirty && ` ${t('languageSwitch.modalUnsavedNote')}`}
        </div>

        <div className="lang-confirm-footer">
          <Button color="secondary" outline onClick={cancelSwitch} className="lang-confirm-btn">
            {t('common.cancel')}
          </Button>
          <Button color="primary" onClick={confirmSwitch} className="lang-confirm-btn lang-confirm-btn--primary">
            {pendingOption
              ? t('languageSwitch.switchTo', { language: pendingOption.nativeName })
              : t('common.continue')}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default LanguageSwitcher;
